
import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import CameraPageShootCardAutoPattern from '@/components/CameraPageShootCardAutoPattern.vue';
import CameraPageShootCardDiagonalHeader from '@/components/CameraPageShootCardDiagonalHeader.vue';
import CameraPageShootCardDiagonalEffect1 from '@/components/CameraPageShootCardDiagonalEffect1.vue';
import CameraPageShootCardDiagonalEffect2 from '@/components/CameraPageShootCardDiagonalEffect2.vue';
import CameraPageShootCardDiagonalEffect3 from '@/components/CameraPageShootCardDiagonalEffect3.vue';
import CameraPageShootCardDiagonalEffect4 from '@/components/CameraPageShootCardDiagonalEffect4.vue';
import CameraPageShootCardDiagonalEffect5 from '@/components/CameraPageShootCardDiagonalEffect5.vue';
import CameraPageShootCardDiagonalEffect6 from '@/components/CameraPageShootCardDiagonalEffect6.vue';
import CameraPageShootCardDiagonalEffect7 from '@/components/CameraPageShootCardDiagonalEffect7.vue';
import CameraPageShootMessageNormal from '@/components/CameraPageShootMessageNormal.vue';
import { is } from '@/util/idDoc';

@Component({
  components: {
    CameraPageShootCardAutoPattern,
    CameraPageShootCardDiagonalHeader,
    CameraPageShootCardDiagonalEffect1,
    CameraPageShootCardDiagonalEffect2,
    CameraPageShootCardDiagonalEffect3,
    CameraPageShootCardDiagonalEffect4,
    CameraPageShootCardDiagonalEffect5,
    CameraPageShootCardDiagonalEffect6,
    CameraPageShootCardDiagonalEffect7,
    CameraPageShootMessageNormal
  }
})
export default class Parts extends Vue {
  private effectStep: number = 0;
  private detectTimerId: number = -1;
  private isTimeout: boolean = false;

  @Prop({ type: Number, required: true })
  public cardWidth!: number;

  @Emit('onDetect')
  private emitDetect() {}

  private get isIdDocFront(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-front';
  }

  private get isIdDocDiagonal(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-diagonal';
  }

  private get isIdDocBack(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-back';
  }

  private get isIdDocDriversLicense(): boolean {
    if (is(this.$store.state.camera.idDocType, 'drivers-license')) return true;
    if (is(this.$store.state.camera.idDocType, 'driving-history-certificate')) return true;

    return false;
  }

  private get isIdDocMyNumberCard(): boolean {
    if (is(this.$store.state.camera.idDocType, 'my-number-card')) return true;

    return false;
  }

  private get effectType(): 'left' | 'right' | 'none' {
    if (this.isIdDocDriversLicense) return 'right';
    if (this.isIdDocMyNumberCard) return 'left';
    return 'none';
  }

  private get status(): 'none' | 'capture' | 'success' {
    if (this.isSuccess) return 'success';
    if (this.isCapture) return 'capture';
    return 'none';
  }

  private get isCapture(): boolean {
    return this.$store.state.camera.isDetectCapture;
  }

  private get isSuccess(): boolean {
    return this.$store.state.camera.isDetectSuccess;
  }

  private get svgSize(): { w: number; h: number } {
    if (this.isIdDocDriversLicense && this.isIdDocFront) return { w: 335, h: 210 };
    if (this.isIdDocDriversLicense && this.isIdDocDiagonal) return { w: 356, h: 140 };
    if (this.isIdDocDriversLicense && this.isIdDocBack) return { w: 335, h: 209 };
    if (this.isIdDocMyNumberCard && this.isIdDocFront) return { w: 335, h: 210 };
    if (this.isIdDocMyNumberCard && this.isIdDocDiagonal) return { w: 356, h: 140 };
    return { w: 0, h: 0 };
  }

  private get frameSize(): { x: number; y: number; w: number; h: number } {
    const w: number = this.cardWidth;
    const h: number = w * (this.svgSize.h / this.svgSize.w);
    const x: number = (this.$store.state.camera.displayWidth - w) / 2;
    const y: number = this.$store.state.camera.displayIdDocCenter - h / 2;
    return { x, y, w, h };
  }

  private get styleFrame(): { [key: string]: string } {
    return {
      left: `${this.frameSize.x}px`,
      top: `${this.frameSize.y}px`,
      width: `${this.frameSize.w}px`,
      height: `${this.frameSize.h}px`
    };
  }

  private get stylePattern(): { [key: string]: string } {
    const x: number = this.frameSize.x;
    const y: number = this.frameSize.y;
    const w: number = this.frameSize.w;
    const h: number = this.frameSize.h;
    const sx: number = w / this.svgSize.w;
    const sy: number = h / this.svgSize.h;
    const cx: number = x + w / 2;
    const isMirror: boolean = this.$store.getters['camera/isMirror'];
    return {
      transform: [
        `translateX(${cx}px) scaleX(${isMirror ? -1 : 1}) translateX(${-cx}px)`,
        `translate(${x}px, ${y}px) scale(${sx}, ${sy})`
      ].join(' ')
    };
  }

  private get styleEffect(): { [key: string]: string } {
    const top: string = `${0}px`;
    const width: string = `${this.frameSize.w}px`;
    const isMirror: boolean = this.$store.getters['camera/isMirror'];
    const transforms: string[] = [];
    transforms.push(`translateY(${top})`);
    transforms.push(`scaleX(${isMirror ? -1 : 1})`);
    return { width, transform: transforms.join(' ') };
  }

  private get warningMessages(): string[] {
    if (this.$store.state.camera.detectWarningCode && this.isTimeout) {
      const key: string = `componentsCameraPageShootCardAuto.${this.$store.state.camera.detectWarningCode}`;
      return this.$t(key) as { [key: number]: string } as string[];
    } else {
      return [];
    }
  }

  private get showCameraModeModal(): boolean {
    return this.$store.state.camera.showCameraModeModal;
  }

  private created(): void {
    this.$store.commit('camera/resetDetectCount');
    this.$store.commit('camera/isDetectCapture', false);
    this.$store.commit('camera/isDetectSuccess', false);
    this.$store.commit('camera/detectWarningCode', '');
  }

  private async mounted(): Promise<void> {
    this.detectTimerId = window.setTimeout(this.handleDetect, 500);

    const isEffect: boolean = this.isIdDocDiagonal;

    if (!isEffect) {
      // 斜撮影演出なし
      this.effectStep = 0;
    } else {
      // 斜撮影演出あり
      const delay1: number = 1000;
      const delay2: number = 600;
      const delay3: number = 1000;
      this.effectStep = 1;
      await new Promise((resolve) => setTimeout(resolve, delay1));
      this.effectStep = 2;
      await new Promise((resolve) => setTimeout(resolve, delay2));
      this.effectStep = 3;
      await new Promise((resolve) => setTimeout(resolve, delay2));
      this.effectStep = 4;
      await new Promise((resolve) => setTimeout(resolve, delay2));
      this.effectStep = 5;
      await new Promise((resolve) => setTimeout(resolve, delay2));
      this.effectStep = 6;
      await new Promise((resolve) => setTimeout(resolve, delay2));
      this.effectStep = 7;
      await new Promise((resolve) => setTimeout(resolve, delay3));
      this.effectStep = 8;
    }

    // 一定期間経過したら演出が変わりメッセージが表示される
    await new Promise((resolve) => setTimeout(resolve, isEffect ? 1500 : 3000));
    this.isTimeout = true;
  }

  private beforeDestroy(): void {
    window.clearTimeout(this.detectTimerId);
  }

  private handleDetect(): void {
    if (this.$store.state.camera.isDetectStop) return;
    // カメラモードの変更中はdetectを止める
    if (this.showCameraModeModal) return;
    if (!this.isSuccess) this.emitDetect();
    this.detectTimerId = window.setTimeout(this.handleDetect, 200);
  }

  @Watch('showCameraModeModal')
  private handleChangeShowingCameraModeModal() {
    // カメラモード変更のモーダルが閉じた時、Detectを再開する
    if (this.showCameraModeModal === false) {
      this.detectTimerId = window.setTimeout(this.handleDetect, 200);
    }
  }
}
